import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { NotaCredito, NotaCreditoCreateRequest } from '../types/notaCredito.types';
import { NotaCreditoService } from '../services/notaCreditoService';

interface NotaCreditoState {
  notas: NotaCredito[];
  selectedNota: NotaCredito | null;
  loading: boolean;
  error: string | null;
  dialogOpen: boolean;

  fetchNotas: () => Promise<void>;
  fetchNotaById: (id: number) => Promise<void>;
  saveNota: (data: NotaCreditoCreateRequest) => Promise<void>;
  deleteNota: (id: number) => Promise<void>;
  setSelectedNota: (nota: NotaCredito | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  notas: [],
  selectedNota: null,
  loading: false,
  error: null,
  dialogOpen: false,
};

export const useNotaCreditoStore = create<NotaCreditoState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchNotas: async () => {
        set({ loading: true, error: null }, false, 'notaCredito/fetchAll/start');
        try {
          const notas = await NotaCreditoService.getAll();
          set({ notas, loading: false }, false, 'notaCredito/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar notas' }, false, 'notaCredito/fetchAll/error');
        }
      },

      fetchNotaById: async (id: number) => {
        set({ loading: true, error: null }, false, 'notaCredito/fetchById/start');
        try {
          const nota = await NotaCreditoService.getById(id);
          set({ selectedNota: nota, loading: false }, false, 'notaCredito/fetchById/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar nota' }, false, 'notaCredito/fetchById/error');
        }
      },

      saveNota: async (data: NotaCreditoCreateRequest) => {
        set({ loading: true, error: null }, false, 'notaCredito/save/start');
        try {
          await NotaCreditoService.save(data);
          set({ loading: false, dialogOpen: false }, false, 'notaCredito/save/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al guardar nota' }, false, 'notaCredito/save/error');
          throw error;
        }
      },

      deleteNota: async (id: number) => {
        set({ loading: true, error: null }, false, 'notaCredito/delete/start');
        try {
          await NotaCreditoService.delete(id);
          set((state) => ({
            notas: state.notas.filter((n) => n.idNotaCredito !== id),
            loading: false,
          }), false, 'notaCredito/delete/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al eliminar nota' }, false, 'notaCredito/delete/error');
        }
      },

      setSelectedNota: (nota) => set({ selectedNota: nota }, false, 'notaCredito/setSelected'),
      openDialog: () => set({ dialogOpen: true }, false, 'notaCredito/openDialog'),
      closeDialog: () => set({ dialogOpen: false, selectedNota: null }, false, 'notaCredito/closeDialog'),
      clearError: () => set({ error: null }, false, 'notaCredito/clearError'),
      reset: () => set(initialState, false, 'notaCredito/reset'),
    }),
    { name: 'nota-credito-store' }
  )
);
